import { cloneElement, isValidElement, useCallback, useEffect, useId, useState } from "react";
import { useSelector } from "react-redux";
import { SponsorshipAPI } from "../../api/SponsorshipAPI";
import { AdvertisementAPI } from "../../api/AdvertisementAPI";
import { PrivilegeAPI } from "../../api/PrivilegesAPI";
import { Privileges } from "../../util/PrivilegeConstants";
import {
    ADS_SPONSORSHIP_FEATURE,
    canAccessAdsFromPrivileges,
    canViewAdsTab,
    hasAdsAction,
    visibleAdsTabs,
} from "../../util/adsPrivileges";
import { bannerPlacementAllowed, isAllowedAssetUrl, placementsForSponsorship } from "../../util/adDisplayRules";
import { placementLabel } from "../../util/adAnalytics";
import { EventAPI } from "../../api/EventAPI";
import { CoachAPI } from "../../api/CoachAPI";
import { dealLabel, humanEnum, optionsForTargetType, TARGET_LABELS, targetFieldLabel } from "../../util/dealTargets";
import { CREATIVE_HINT, validateCreativeFile } from "../../util/creativeUpload";
import {
    ADS_LIMITS,
    adsApiMessage,
    campaignPayload,
    clubUploadMessage,
    toLocalDateTime,
    validateBannerForm,
    validateCampaignForm,
    validateDealForm,
    validatePaymentForm,
    validateSponsorForm,
} from "../../util/adsFormValidation";
import AdsGuide from "./AdsGuide";
import AdsSlotPreview from "./AdsSlotPreview";
import AdsAppPreview from "./AdsAppPreview.jsx";
import AdsTierCard from "./AdsTierCard.jsx";
import { screenForPlacement } from "../../util/adsPhonePreview";
import { ADS_TAB_COPY } from "../../util/adsCopy";
import { ADS_PAGE_SIZE, collectPagedContent } from "../../util/adsPaging";
import CustomDialog from "../CustomComponents/CustomDialog/CustomDialog.jsx";
import "./AdsSponsorship.css";

const TARGET_TYPES = ["EVENT", "TEAM", "PLAYER", "EVENT_PLAYER", "BANNER"];
const TIERS = ["GOLD", "SILVER", "BRONZE"];
const PAY_METHODS = ["BANK_TRANSFER", "CREDIT_CARD", "CASH", "CHECK"];
const PAY_STATUSES = ["PENDING", "COMPLETED", "FAILED", "REFUNDED"];
const CAMPAIGN_STATUSES = ["DRAFT", "ACTIVE", "PAUSED", "COMPLETED", "CANCELLED"];

const emptyBannerForm = () => ({
    title: "",
    imageUrl: "",
    targetUrl: "",
    placement: "HOME_SCREEN",
    active: true,
    sponsorshipId: "",
    adType: "IMAGE",
    videoUrl: "",
    campaignStartDate: "",
    campaignEndDate: "",
    dailyBudget: "",
    targetAudience: "",
    priority: 0,
    campaignId: "",
});

const emptySponsorForm = () => ({
    name: "",
    logoUrl: "",
    contactEmail: "",
    contactPerson: "",
    companyWebsite: "",
    description: "",
});

const bannerIdOf = (banner) => banner.id || banner.bannerId;
const campaignIdOf = (campaign) => campaign.id || campaign.campaignId;
const sponsorIdOf = (sponsor) => sponsor.id || sponsor.sponsorId;
const dealIdOf = (deal) => deal.id || deal.sponsorshipId;
const paymentIdOf = (payment) => payment.id || payment.paymentId;

const emptyDealForm = () => ({
    sponsorId: "",
    targetType: "EVENT",
    targetId: "",
    startDate: "",
    endDate: "",
    amount: "",
    tier: "GOLD",
    contractUrl: "",
});

const emptyPaymentForm = () => ({
    sponsorshipId: "",
    paymentDate: "",
    amount: "",
    paymentMethod: "BANK_TRANSFER",
    status: "PENDING",
    transactionId: "",
    notes: "",
    receiptUrl: "",
});

const emptyConfirm = () => ({ open: false, title: "", message: "", action: null });

const emptyCampaignForm = () => ({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    totalBudget: "",
    dailyBudget: "",
    status: "DRAFT",
    targetAudience: "",
});

const Field = ({ label, hint, error, required, children }) => {
    const id = useId();
    const control = isValidElement(children)
        ? cloneElement(children, {
            id: children.props.id || id,
            "aria-invalid": children.props["aria-invalid"] ?? Boolean(error),
        })
        : children;
    return (
        <div className="ads-field">
            <span className="ads-label">
                <label htmlFor={children.props?.id || id}>{label}</label>
                {required ? <span className="ads-required">Required</span> : null}
            </span>
            {control}
            {error ? <span className="ads-field-error">{error}</span> : hint ? <span className="ads-hint">{hint}</span> : null}
        </div>
    );
};

const AdsSponsorship = () => {
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const storeClubId = useSelector((store) => store.user.clubId);
    const clubId = selectedClub?.clubId || storeClubId;

    const [tab, setTab] = useState("Sponsors");
    const [error, setError] = useState("");
    const [sponsors, setSponsors] = useState([]);
    const [sponsorships, setSponsorships] = useState([]);
    const [payments, setPayments] = useState([]);
    const [banners, setBanners] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [previewScreen, setPreviewScreen] = useState("home");
    const [creativeFile, setCreativeFile] = useState(null);
    const [sponsorLogoFile, setSponsorLogoFile] = useState(null);
    const [editingBannerId, setEditingBannerId] = useState("");
    const [editingSponsorId, setEditingSponsorId] = useState("");
    const [dealCatalogs, setDealCatalogs] = useState({ events: [], teams: [], players: [], eventPlayers: [] });
    const [eventPlayerEventId, setEventPlayerEventId] = useState("");

    const [sponsorForm, setSponsorForm] = useState(emptySponsorForm);
    const [dealForm, setDealForm] = useState(emptyDealForm);
    const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
    const [bannerForm, setBannerForm] = useState(emptyBannerForm);
    const [campaignForm, setCampaignForm] = useState(emptyCampaignForm);
    const [editingCampaignId, setEditingCampaignId] = useState("");
    const [editingDealId, setEditingDealId] = useState("");
    const [editingPaymentId, setEditingPaymentId] = useState("");
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(emptyConfirm);
    const [fieldErrors, setFieldErrors] = useState({});
    const [privileges, setPrivileges] = useState(null);
    const loadDisplay = useCallback(
        (placement) => AdvertisementAPI.getDisplayBanners(placement, clubId),
        [clubId]
    );

    const ads = Privileges.ADS_SPONSORSHIP;
    const canDo = (subFeature, action) => hasAdsAction(privileges, subFeature, action);

    const loadAll = async (nextPrivileges = privileges) => {
        if (!clubId || !canAccessAdsFromPrivileges(nextPrivileges)) {
            return;
        }
        const empty = { Data: [] };
        try {
            const [sponsorRes, deals, pays, bannerRes, campaignRes] = await Promise.all([
                canViewAdsTab(nextPrivileges, ads.MANAGE_SPONSOR) ? SponsorshipAPI.getSponsorsByClub(clubId) : empty,
                canViewAdsTab(nextPrivileges, ads.MANAGE_SPONSORSHIP)
                    ? collectPagedContent((page, size) => SponsorshipAPI.getSponsorshipsByClub(clubId, page, size), ADS_PAGE_SIZE)
                    : Promise.resolve([]),
                canViewAdsTab(nextPrivileges, ads.MANAGE_SPONSORSHIP_PAYMENT)
                    ? collectPagedContent((page, size) => SponsorshipAPI.getPaymentsByClub(clubId, page, size), ADS_PAGE_SIZE)
                    : Promise.resolve([]),
                canViewAdsTab(nextPrivileges, ads.MANAGE_ADVERTISEMENT) ? AdvertisementAPI.getBannersByClub(clubId) : empty,
                canViewAdsTab(nextPrivileges, ads.MANAGE_ADVERTISEMENT) ? AdvertisementAPI.getCampaignsByClub(clubId) : empty,
            ]);
            setSponsors(Array.isArray(sponsorRes.Data) ? sponsorRes.Data : []);
            setSponsorships(deals);
            setPayments(pays);
            setBanners(bannerRes.Data || []);
            setCampaigns(campaignRes.Data || []);
        } catch (err) {
            setError(adsApiMessage(err));
        }
    };

    const loadDealCatalogs = async () => {
        if (!clubId) {
            return;
        }
        const [eventRes, teamRes, playerRes] = await Promise.all([
            EventAPI.getAllEventForListOptions(clubId),
            CoachAPI.findAllTeamByClubId(clubId),
            CoachAPI.findAllClubPlayer(clubId),
        ]);
        setDealCatalogs({
            events: Array.isArray(eventRes.Data) ? eventRes.Data : [],
            teams: Array.isArray(teamRes.Data) ? teamRes.Data : [],
            players: Array.isArray(playerRes.Data) ? playerRes.Data : [],
            eventPlayers: [],
        });
    };

    const loadEventPlayers = async (eventId) => {
        if (!clubId || !eventId) {
            setDealCatalogs((prev) => ({ ...prev, eventPlayers: [] }));
            return;
        }
        const result = await EventAPI.getAllEventPlayer(clubId, eventId, 200, 0, "", {});
        setDealCatalogs((prev) => ({
            ...prev,
            eventPlayers: Array.isArray(result.Data) ? result.Data : [],
        }));
    };

    useEffect(() => {
        if (!userInfo?.userId) {
            return;
        }
        PrivilegeAPI.getAllPrivilegesByFeature({
            userId: userInfo.userId,
            featureName: ADS_SPONSORSHIP_FEATURE,
        })
            .then((response) => {
                const next = response.Data || {};
                setPrivileges(next);
                const firstTab = visibleAdsTabs(next)[0];
                if (firstTab) {
                    setTab(firstTab.name);
                }
                return loadAll(next);
            })
            .catch((err) => {
                setPrivileges({});
                setError(adsApiMessage(err));
            });
    }, [clubId, userInfo?.userId]);

    if (privileges == null) {
        return (
            <div className="ads-page" data-testid="ads-sponsorship-page">
                <p className="ads-kicker">Advertising</p>
                <h1>Ads & sponsorship</h1>
                <p className="ads-lead">Checking access…</p>
            </div>
        );
    }

    if (!canAccessAdsFromPrivileges(privileges)) {
        return (
            <div className="ads-page ads-denied" data-testid="ads-access-denied">
                <p className="ads-kicker">Advertising</p>
                <h1>Ads & sponsorship</h1>
                <p className="ads-lead">You need an ads or sponsorship privilege to open this page. Ask a club owner to assign it in Access Management.</p>
            </div>
        );
    }

    const allowedPlacements = placementsForSponsorship(bannerForm.sponsorshipId, sponsorships);

    const onField = (setter) => (event) => {
        const { name, value, type, checked } = event.target;
        setter((prev) => {
            const next = { ...prev, [name]: type === "checkbox" ? checked : value };
            if (setter === setBannerForm && name === "sponsorshipId") {
                const allowed = placementsForSponsorship(next.sponsorshipId, sponsorships);
                if (!allowed.includes(next.placement)) {
                    next.placement = allowed[0];
                }
            }
            if (setter === setDealForm && name === "targetType") {
                next.targetId = "";
                setEventPlayerEventId("");
                setDealCatalogs((prev) => ({ ...prev, eventPlayers: [] }));
            }
            return next;
        });
    };

    const applyValidation = (result) => {
        setFieldErrors(result.fields);
        setError(result.message);
        return result.ok;
    };

    const saveSponsor = async (event) => {
        event.preventDefault();
        if (saving || !applyValidation(validateSponsorForm(sponsorForm))) {
            return;
        }
        setSaving(true);
        try {
            const body = { ...sponsorForm, clubId };
            if (editingSponsorId) {
                await SponsorshipAPI.updateSponsor(editingSponsorId, clubId, body);
            } else {
                await SponsorshipAPI.createSponsor(body);
            }
            setEditingSponsorId("");
            setSponsorForm(emptySponsorForm());
            setSponsorLogoFile(null);
            setFieldErrors({});
            await loadAll();
        } catch (err) {
            setError(adsApiMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const editSponsor = (sponsor) => {
        setEditingSponsorId(sponsorIdOf(sponsor));
        setSponsorForm({
            ...emptySponsorForm(),
            name: sponsor.name || "",
            logoUrl: sponsor.logoUrl || "",
            contactEmail: sponsor.contactEmail || "",
            contactPerson: sponsor.contactPerson || "",
            companyWebsite: sponsor.companyWebsite || "",
            description: sponsor.description || "",
        });
        setSponsorLogoFile(null);
        setFieldErrors({});
        setError("");
    };

    const dealBody = () => ({
        sponsorId: dealForm.sponsorId,
        targets: [{ targetType: dealForm.targetType, targetId: dealForm.targetId }],
        startDate: dealForm.startDate || null,
        endDate: dealForm.endDate || null,
        amount: dealForm.amount ? Number(dealForm.amount) : null,
        tier: dealForm.tier,
        contractUrl: dealForm.contractUrl || null,
        clubId,
    });

    const editDeal = (deal) => {
        const target = deal.targets?.[0] || {};
        setEditingDealId(dealIdOf(deal));
        setDealForm({
            ...emptyDealForm(),
            sponsorId: deal.sponsorId || "",
            targetType: target.targetType || "EVENT",
            targetId: target.targetId || "",
            startDate: deal.startDate || "",
            endDate: deal.endDate || "",
            amount: deal.amount != null ? String(deal.amount) : "",
            tier: deal.tier || "GOLD",
            contractUrl: deal.contractUrl || "",
        });
        setFieldErrors({});
        setError("");
    };

    const saveDeal = async (event) => {
        event.preventDefault();
        if (saving || !applyValidation(validateDealForm(dealForm))) {
            return;
        }
        setSaving(true);
        try {
            if (editingDealId) {
                await SponsorshipAPI.updateSponsorship(editingDealId, clubId, dealBody());
            } else {
                await SponsorshipAPI.createSponsorship(dealBody());
            }
            setEditingDealId("");
            setDealForm(emptyDealForm());
            setFieldErrors({});
            await loadAll();
        } catch (err) {
            setError(adsApiMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const paymentBody = () => ({
        ...paymentForm,
        amount: Number(paymentForm.amount),
        clubId,
    });

    const editPayment = (payment) => {
        setEditingPaymentId(paymentIdOf(payment));
        setPaymentForm({
            ...emptyPaymentForm(),
            sponsorshipId: payment.sponsorshipId || "",
            paymentDate: payment.paymentDate || "",
            amount: payment.amount != null ? String(payment.amount) : "",
            paymentMethod: payment.paymentMethod || "BANK_TRANSFER",
            status: payment.status || "PENDING",
            transactionId: payment.transactionId || "",
            notes: payment.notes || "",
            receiptUrl: payment.receiptUrl || "",
        });
        setFieldErrors({});
        setError("");
    };

    const savePayment = async (event) => {
        event.preventDefault();
        if (saving || !applyValidation(validatePaymentForm(paymentForm))) {
            return;
        }
        setSaving(true);
        try {
            if (editingPaymentId) {
                await SponsorshipAPI.updatePayment(editingPaymentId, clubId, paymentBody());
            } else {
                await SponsorshipAPI.createPayment(paymentBody());
            }
            setEditingPaymentId("");
            setPaymentForm(emptyPaymentForm());
            setFieldErrors({});
            await loadAll();
        } catch (err) {
            setError(adsApiMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const saveBanner = async (event) => {
        event.preventDefault();
        if (saving || !applyValidation(validateBannerForm(bannerForm, {
            placementAllowed: bannerPlacementAllowed(bannerForm.sponsorshipId, bannerForm.placement, sponsorships),
        }))) {
            return;
        }
        const bannerBody = {
            title: bannerForm.title,
            imageUrl: bannerForm.imageUrl,
            targetUrl: bannerForm.targetUrl || null,
            placement: bannerForm.placement,
            active: bannerForm.active,
            sponsorshipId: bannerForm.sponsorshipId || null,
            clubId,
            adType: bannerForm.adType,
            videoUrl: bannerForm.videoUrl || null,
            campaignStartDate: bannerForm.campaignStartDate || null,
            campaignEndDate: bannerForm.campaignEndDate || null,
            dailyBudget: bannerForm.dailyBudget ? Number(bannerForm.dailyBudget) : null,
            targetAudience: bannerForm.targetAudience || null,
            priority: Number(bannerForm.priority) || 0,
            campaignId: bannerForm.campaignId || null,
        };
        setSaving(true);
        try {
            if (editingBannerId) {
                await AdvertisementAPI.updateBanner(editingBannerId, clubId, bannerBody);
            } else {
                await AdvertisementAPI.createBanner(bannerBody);
            }
            setEditingBannerId("");
            setBannerForm(emptyBannerForm());
            setFieldErrors({});
            await loadAll();
        } catch (err) {
            setError(adsApiMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const editBanner = (banner) => {
        const sponsorshipId = banner.sponsorshipId || "";
        const allowed = placementsForSponsorship(sponsorshipId, sponsorships);
        setEditingBannerId(bannerIdOf(banner));
        setBannerForm({
            ...emptyBannerForm(),
            title: banner.title || "",
            imageUrl: banner.imageUrl || "",
            targetUrl: banner.targetUrl || "",
            placement: allowed.includes(banner.placement) ? banner.placement : allowed[0],
            active: banner.active !== false,
            sponsorshipId,
            adType: banner.adType || "IMAGE",
            videoUrl: banner.videoUrl || "",
            campaignId: banner.campaignId || "",
        });
        setFieldErrors({});
        setError("");
    };

    const runListedAction = async (action) => {
        if (saving) {
            return;
        }
        setSaving(true);
        setError("");
        try {
            await action();
            await loadAll();
        } catch (err) {
            setError(adsApiMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const askDelete = (title, message, action) => {
        setConfirmDelete({ open: true, title, message, action });
    };

    const closeConfirm = () => setConfirmDelete(emptyConfirm());

    const confirmDeleteNow = async () => {
        const action = confirmDelete.action;
        closeConfirm();
        if (action) {
            await runListedAction(action);
        }
    };

    const saveCampaign = async (event) => {
        event.preventDefault();
        if (saving || !applyValidation(validateCampaignForm(campaignForm))) {
            return;
        }
        setSaving(true);
        try {
            const body = campaignPayload(campaignForm, clubId);
            if (editingCampaignId) {
                await AdvertisementAPI.updateCampaign(editingCampaignId, clubId, body);
            } else {
                await AdvertisementAPI.createCampaign(body);
            }
            setEditingCampaignId("");
            setCampaignForm(emptyCampaignForm());
            setFieldErrors({});
            await loadAll();
        } catch (err) {
            setError(adsApiMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const editCampaign = (campaign) => {
        setEditingCampaignId(campaignIdOf(campaign));
        setCampaignForm({
            ...emptyCampaignForm(),
            name: campaign.name || "",
            description: campaign.description || "",
            startDate: toLocalDateTime(campaign.startDate),
            endDate: toLocalDateTime(campaign.endDate),
            totalBudget: campaign.totalBudget != null ? String(campaign.totalBudget) : "",
            dailyBudget: campaign.dailyBudget != null ? String(campaign.dailyBudget) : "",
            status: campaign.status || "DRAFT",
            targetAudience: campaign.targetAudience || "",
        });
        setFieldErrors({});
        setError("");
    };

    const uploadCreative = async () => {
        if (saving) {
            return;
        }
        const check = validateCreativeFile(creativeFile);
        if (!check.ok) {
            setFieldErrors((prev) => ({ ...prev, imageUrl: check.message }));
            setError(check.message);
            return;
        }
        setError("");
        setSaving(true);
        try {
            const result = await AdvertisementAPI.uploadCreative(creativeFile, clubId);
            const imageUrl = result.Data?.imageUrl;
            if (imageUrl) {
                setBannerForm((prev) => ({ ...prev, imageUrl }));
                setFieldErrors((prev) => ({ ...prev, imageUrl: undefined }));
            }
        } catch (err) {
            setError(clubUploadMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const uploadSponsorLogo = async () => {
        if (saving || !canDo(ads.MANAGE_ADVERTISEMENT, Privileges.CREATE)) {
            return;
        }
        const check = validateCreativeFile(sponsorLogoFile);
        if (!check.ok) {
            setFieldErrors((prev) => ({ ...prev, logoUrl: check.message }));
            setError(check.message);
            return;
        }
        setError("");
        setSaving(true);
        try {
            const result = await AdvertisementAPI.uploadCreative(sponsorLogoFile, clubId);
            const imageUrl = result.Data?.imageUrl;
            if (imageUrl) {
                setSponsorForm((prev) => ({ ...prev, logoUrl: imageUrl }));
                setFieldErrors((prev) => ({ ...prev, logoUrl: undefined }));
            }
        } catch (err) {
            setError(clubUploadMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const openTab = (name) => {
        setTab(name);
        setFieldErrors({});
        setError("");
        if (name === "Deals") {
            loadDealCatalogs().catch((err) => setError(adsApiMessage(err)));
        }
    };

    const previewBanners = () => {
        setPreviewScreen(screenForPlacement(bannerForm.placement));
        openTab("Preview");
    };

    return (
        <div className="ads-page" data-testid="ads-sponsorship-page">
            <p className="ads-kicker">Advertising</p>
            <h1>Ads & sponsorship</h1>
            <AdsGuide tab={tab} visibleTabs={visibleAdsTabs(privileges)} onSelect={openTab} />
            {error && <p className="ads-error" role="alert">{error}</p>}

            <div className="ads-tabs" role="tablist">
                {visibleAdsTabs(privileges).map((item) => (
                    <button
                        key={item.name}
                        type="button"
                        className="ads-tab"
                        role="tab"
                        aria-selected={tab === item.name}
                        onClick={() => {
                            if (item.name === "Preview") {
                                setPreviewScreen("home");
                            }
                            openTab(item.name);
                        }}
                    >
                        {item.name}
                    </button>
                ))}
            </div>

            {tab === "Sponsors" && (
                <div className="ads-panel">
                    {(canDo(ads.MANAGE_SPONSOR, Privileges.CREATE) || (editingSponsorId && canDo(ads.MANAGE_SPONSOR, Privileges.MODIFY))) && (
                    <form className="ads-card ads-form" onSubmit={saveSponsor} noValidate>
                        <div>
                            <h2>{editingSponsorId ? "Edit sponsor" : "Add sponsor"}</h2>
                            <p className="ads-card-lead">{ADS_TAB_COPY.Sponsors.how}</p>
                        </div>
                        <Field label="Sponsor name" required error={fieldErrors.name} hint={`Max ${ADS_LIMITS.NAME} characters.`}>
                            <input name="name" maxLength={ADS_LIMITS.NAME} value={sponsorForm.name} onChange={onField(setSponsorForm)} aria-invalid={Boolean(fieldErrors.name)} />
                        </Field>
                        {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.CREATE) && (
                            <>
                                <div className="ads-field">
                                    <span className="ads-label">
                                        <label htmlFor="ads-sponsor-logo-file">Sponsor logo</label>
                                    </span>
                                    <div className="ads-file">
                                        <input
                                            id="ads-sponsor-logo-file"
                                            className="ads-file-input"
                                            type="file"
                                            accept="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                                            aria-label="Sponsor logo"
                                            onChange={(event) => {
                                                const file = event.target.files?.[0] || null;
                                                const check = validateCreativeFile(file);
                                                if (file && !check.ok) {
                                                    setFieldErrors((prev) => ({ ...prev, logoUrl: check.message }));
                                                    setError(check.message);
                                                    setSponsorLogoFile(null);
                                                    event.target.value = "";
                                                    return;
                                                }
                                                setError("");
                                                setSponsorLogoFile(file);
                                            }}
                                        />
                                        <label htmlFor="ads-sponsor-logo-file" className="ads-btn ads-btn-secondary">Choose image</label>
                                        <span className="ads-file-name">{sponsorLogoFile?.name || "No file chosen"}</span>
                                    </div>
                                    <span className="ads-hint">{CREATIVE_HINT}</span>
                                </div>
                                <div className="ads-actions">
                                    <button type="button" className="ads-btn ads-btn-secondary" disabled={saving} onClick={uploadSponsorLogo}>Upload logo</button>
                                </div>
                            </>
                        )}
                        {isAllowedAssetUrl(sponsorForm.logoUrl) && sponsorForm.logoUrl ? (
                            <div data-testid="sponsor-logo-ready">
                                <AdsSlotPreview imageUrl={sponsorForm.logoUrl} placement="SPONSOR_SECTION" title={sponsorForm.name || "Sponsor logo"} />
                            </div>
                        ) : null}
                        <Field label="Logo URL" hint="Optional if you uploaded a logo. Otherwise use https." error={fieldErrors.logoUrl}>
                            <input name="logoUrl" maxLength={ADS_LIMITS.URL} value={sponsorForm.logoUrl} onChange={onField(setSponsorForm)} placeholder="https://" aria-invalid={Boolean(fieldErrors.logoUrl)} />
                        </Field>
                        <Field label="Contact email" error={fieldErrors.contactEmail}>
                            <input name="contactEmail" type="email" maxLength={ADS_LIMITS.EMAIL} value={sponsorForm.contactEmail} onChange={onField(setSponsorForm)} aria-invalid={Boolean(fieldErrors.contactEmail)} />
                        </Field>
                        <Field label="Contact person" error={fieldErrors.contactPerson}>
                            <input name="contactPerson" maxLength={ADS_LIMITS.PERSON} value={sponsorForm.contactPerson} onChange={onField(setSponsorForm)} />
                        </Field>
                        <Field label="Company website" error={fieldErrors.companyWebsite}>
                            <input name="companyWebsite" maxLength={ADS_LIMITS.URL} value={sponsorForm.companyWebsite} onChange={onField(setSponsorForm)} placeholder="https://" aria-invalid={Boolean(fieldErrors.companyWebsite)} />
                        </Field>
                        <Field label="Description" hint={`Max ${ADS_LIMITS.DESCRIPTION} characters.`} error={fieldErrors.description}>
                            <textarea name="description" maxLength={ADS_LIMITS.DESCRIPTION} value={sponsorForm.description} onChange={onField(setSponsorForm)} />
                        </Field>
                        <div className="ads-actions ads-actions-end">
                            <button type="submit" className="ads-btn ads-btn-primary" disabled={saving}>{editingSponsorId ? "Update sponsor" : "Save sponsor"}</button>
                            {editingSponsorId && (
                                <button
                                    type="button"
                                    className="ads-btn ads-btn-secondary"
                                    onClick={() => {
                                        setEditingSponsorId("");
                                        setSponsorForm(emptySponsorForm());
                                        setSponsorLogoFile(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                    )}
                    <div className="ads-card">
                        <h2>Sponsors</h2>
                        <p className="ads-card-lead">{ADS_TAB_COPY.Sponsors.meaning}</p>
                        {sponsors.length === 0 ? (
                            <p className="ads-empty-copy" data-testid="sponsors-empty">No sponsors yet.</p>
                        ) : (
                            <ul className="ads-list">
                                {sponsors.map((sponsor) => (
                                    <li key={sponsorIdOf(sponsor)}>
                                        <span className="ads-list-copy">{sponsor.name}</span>
                                        <span className="ads-actions">
                                            {canDo(ads.MANAGE_SPONSOR, Privileges.MODIFY) && (
                                                <button type="button" className="ads-btn ads-btn-ghost" onClick={() => editSponsor(sponsor)}>Edit</button>
                                            )}
                                            {canDo(ads.MANAGE_SPONSOR, Privileges.DELETE) && (
                                                <button
                                                    type="button"
                                                    className="ads-btn ads-btn-danger"
                                                    disabled={saving}
                                                    onClick={() => askDelete(
                                                        "Delete sponsor?",
                                                        `Delete ${sponsor.name || "this sponsor"}? This cannot be undone.`,
                                                        () => SponsorshipAPI.deleteSponsor(sponsorIdOf(sponsor), clubId)
                                                    )}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {tab === "Deals" && (
                <div className="ads-panel">
                    {(canDo(ads.MANAGE_SPONSORSHIP, Privileges.CREATE) || (editingDealId && canDo(ads.MANAGE_SPONSORSHIP, Privileges.MODIFY))) && (
                    <form className="ads-card ads-form" onSubmit={saveDeal} noValidate>
                        <div>
                            <h2>{editingDealId ? "Edit deal" : "Add deal"}</h2>
                            <p className="ads-card-lead">{ADS_TAB_COPY.Deals.how}</p>
                        </div>
                        <Field label="Sponsor" required error={fieldErrors.sponsorId}>
                            <select name="sponsorId" value={dealForm.sponsorId} onChange={onField(setDealForm)} aria-invalid={Boolean(fieldErrors.sponsorId)}>
                                <option value="">Select sponsor</option>
                                {sponsors.map((sponsor) => <option key={sponsor.id} value={sponsor.id}>{sponsor.name}</option>)}
                            </select>
                        </Field>
                        <label className="ads-field">
                            <span className="ads-label">Attach to</span>
                            <select name="targetType" value={dealForm.targetType} onChange={onField(setDealForm)}>
                                {TARGET_TYPES.map((type) => <option key={type} value={type}>{TARGET_LABELS[type]}</option>)}
                            </select>
                        </label>
                        {dealForm.targetType === "EVENT_PLAYER" && (
                            <label className="ads-field">
                                <span className="ads-label">Event</span>
                                <select
                                    name="eventPlayerEventId"
                                    value={eventPlayerEventId}
                                    onChange={(event) => {
                                        const eventId = event.target.value;
                                        setEventPlayerEventId(eventId);
                                        setDealForm((prev) => ({ ...prev, targetId: "" }));
                                        loadEventPlayers(eventId).catch((err) => setError(adsApiMessage(err)));
                                    }}
                                >
                                    <option value="">Select event</option>
                                    {dealCatalogs.events.map((eventItem) => (
                                        <option key={eventItem.eventId} value={eventItem.eventId}>{eventItem.eventName}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <Field label={targetFieldLabel(dealForm.targetType)} required error={fieldErrors.targetId}>
                            <select name="targetId" value={dealForm.targetId} onChange={onField(setDealForm)} aria-invalid={Boolean(fieldErrors.targetId)}>
                                <option value="">Select {targetFieldLabel(dealForm.targetType).toLowerCase()}</option>
                                {optionsForTargetType(dealForm.targetType, { ...dealCatalogs, banners }).map((option) => (
                                    <option key={option.id} value={option.id}>{option.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Start date">
                            <input name="startDate" type="date" value={dealForm.startDate} onChange={onField(setDealForm)} />
                        </Field>
                        <Field label="End date" error={fieldErrors.endDate}>
                            <input name="endDate" type="date" value={dealForm.endDate} onChange={onField(setDealForm)} aria-invalid={Boolean(fieldErrors.endDate)} />
                        </Field>
                        <Field label="Amount" error={fieldErrors.amount}>
                            <input name="amount" type="number" min="0" max={ADS_LIMITS.MONEY_MAX} step="0.01" value={dealForm.amount} onChange={onField(setDealForm)} aria-invalid={Boolean(fieldErrors.amount)} />
                        </Field>
                        <Field label="Tier" hint="GOLD, SILVER, and BRONZE are explained in the card next to this form.">
                            <select name="tier" value={dealForm.tier} onChange={onField(setDealForm)}>
                                {TIERS.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                            </select>
                        </Field>
                        <Field label="Contract URL" error={fieldErrors.contractUrl}>
                            <input name="contractUrl" maxLength={ADS_LIMITS.URL} value={dealForm.contractUrl} onChange={onField(setDealForm)} placeholder="https://" aria-invalid={Boolean(fieldErrors.contractUrl)} />
                        </Field>
                        <div className="ads-actions ads-actions-end">
                            <button type="submit" className="ads-btn ads-btn-primary" disabled={saving}>{editingDealId ? "Update deal" : "Save deal"}</button>
                            {editingDealId && (
                                <button
                                    type="button"
                                    className="ads-btn ads-btn-secondary"
                                    onClick={() => {
                                        setEditingDealId("");
                                        setDealForm(emptyDealForm());
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                    )}
                    <div className="ads-stack">
                    <AdsTierCard isFreePlan={selectedClub?.freePlanUser !== false} />
                    <div className="ads-card">
                        <h2>Deals</h2>
                        <p className="ads-card-lead">{ADS_TAB_COPY.Deals.meaning}</p>
                        {sponsorships.length === 0 ? <p className="ads-empty-copy">No deals yet.</p> : (
                            <ul className="ads-list">
                                {sponsorships.map((deal) => (
                                    <li key={dealIdOf(deal)}>
                                        <span className="ads-list-copy">{dealLabel(deal, sponsors)}{deal.amount != null ? ` · ${deal.amount}` : ""}</span>
                                        <span className="ads-actions">
                                            {canDo(ads.MANAGE_SPONSORSHIP, Privileges.MODIFY) && (
                                                <button type="button" className="ads-btn ads-btn-ghost" disabled={saving} onClick={() => editDeal(deal)}>Edit</button>
                                            )}
                                            {canDo(ads.MANAGE_SPONSORSHIP, Privileges.DELETE) && (
                                                <button
                                                    type="button"
                                                    className="ads-btn ads-btn-danger"
                                                    disabled={saving}
                                                    onClick={() => askDelete(
                                                        "Delete deal?",
                                                        `Delete ${dealLabel(deal, sponsors)}? This cannot be undone.`,
                                                        () => SponsorshipAPI.deleteSponsorship(dealIdOf(deal), clubId)
                                                    )}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    </div>
                </div>
            )}

            {tab === "Payments" && (
                <div className="ads-panel">
                    {(canDo(ads.MANAGE_SPONSORSHIP_PAYMENT, Privileges.CREATE) || (editingPaymentId && canDo(ads.MANAGE_SPONSORSHIP_PAYMENT, Privileges.MODIFY))) && (
                    <form className="ads-card ads-form" onSubmit={savePayment} noValidate>
                        <div>
                            <h2>{editingPaymentId ? "Edit payment" : "Record payment"}</h2>
                            <p className="ads-card-lead">{ADS_TAB_COPY.Payments.how}</p>
                        </div>
                        <Field label="Deal" required error={fieldErrors.sponsorshipId}>
                            <select name="sponsorshipId" value={paymentForm.sponsorshipId} onChange={onField(setPaymentForm)} aria-invalid={Boolean(fieldErrors.sponsorshipId)}>
                                <option value="">Select deal</option>
                                {sponsorships.map((deal) => <option key={deal.id} value={deal.id}>{dealLabel(deal, sponsors)}</option>)}
                            </select>
                        </Field>
                        <Field label="Payment date" required error={fieldErrors.paymentDate}>
                            <input name="paymentDate" type="date" value={paymentForm.paymentDate} onChange={onField(setPaymentForm)} aria-invalid={Boolean(fieldErrors.paymentDate)} />
                        </Field>
                        <Field label="Amount" required error={fieldErrors.amount}>
                            <input name="amount" type="number" min="0.01" max={ADS_LIMITS.MONEY_MAX} step="0.01" value={paymentForm.amount} onChange={onField(setPaymentForm)} aria-invalid={Boolean(fieldErrors.amount)} />
                        </Field>
                        <Field label="Method">
                            <select name="paymentMethod" value={paymentForm.paymentMethod} onChange={onField(setPaymentForm)}>
                                {PAY_METHODS.map((method) => <option key={method} value={method}>{humanEnum(method)}</option>)}
                            </select>
                        </Field>
                        <Field label="Status">
                            <select name="status" value={paymentForm.status} onChange={onField(setPaymentForm)}>
                                {PAY_STATUSES.map((status) => <option key={status} value={status}>{humanEnum(status)}</option>)}
                            </select>
                        </Field>
                        <Field label="Transaction ID" error={fieldErrors.transactionId}>
                            <input name="transactionId" maxLength={ADS_LIMITS.TRANSACTION_ID} value={paymentForm.transactionId} onChange={onField(setPaymentForm)} aria-invalid={Boolean(fieldErrors.transactionId)} />
                        </Field>
                        <Field label="Notes" hint={`Max ${ADS_LIMITS.NOTES} characters.`} error={fieldErrors.notes}>
                            <textarea name="notes" maxLength={ADS_LIMITS.NOTES} value={paymentForm.notes} onChange={onField(setPaymentForm)} />
                        </Field>
                        <Field label="Receipt URL" error={fieldErrors.receiptUrl}>
                            <input name="receiptUrl" maxLength={ADS_LIMITS.URL} value={paymentForm.receiptUrl} onChange={onField(setPaymentForm)} placeholder="https://" aria-invalid={Boolean(fieldErrors.receiptUrl)} />
                        </Field>
                        <div className="ads-actions ads-actions-end">
                            <button type="submit" className="ads-btn ads-btn-primary" disabled={saving}>{editingPaymentId ? "Update payment" : "Save payment"}</button>
                            {editingPaymentId && (
                                <button
                                    type="button"
                                    className="ads-btn ads-btn-secondary"
                                    onClick={() => {
                                        setEditingPaymentId("");
                                        setPaymentForm(emptyPaymentForm());
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                    )}
                    <div className="ads-card">
                        <h2>Payments</h2>
                        <p className="ads-card-lead">{ADS_TAB_COPY.Payments.meaning}</p>
                        {payments.length === 0 ? <p className="ads-empty-copy">No payments yet.</p> : (
                            <ul className="ads-list">
                                {payments.map((payment) => (
                                    <li key={paymentIdOf(payment)}>
                                        <span className="ads-list-copy">{payment.amount} · {payment.status || payment.paymentMethod}</span>
                                        <span className="ads-actions">
                                            {canDo(ads.MANAGE_SPONSORSHIP_PAYMENT, Privileges.MODIFY) && (
                                                <button type="button" className="ads-btn ads-btn-ghost" disabled={saving} onClick={() => editPayment(payment)}>Edit</button>
                                            )}
                                            {canDo(ads.MANAGE_SPONSORSHIP_PAYMENT, Privileges.DELETE) && (
                                                <button
                                                    type="button"
                                                    className="ads-btn ads-btn-danger"
                                                    disabled={saving}
                                                    onClick={() => askDelete(
                                                        "Delete payment?",
                                                        "Delete this payment record? This cannot be undone.",
                                                        () => SponsorshipAPI.deletePayment(paymentIdOf(payment), clubId)
                                                    )}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {tab === "Banners" && (
                <div className="ads-panel">
                    {(canDo(ads.MANAGE_ADVERTISEMENT, Privileges.CREATE) || (editingBannerId && canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY))) && (
                    <form className="ads-card ads-form" onSubmit={saveBanner} noValidate>
                        <div>
                            <h2>{editingBannerId ? "Edit banner" : "Add banner"}</h2>
                            <p className="ads-card-lead">{ADS_TAB_COPY.Banners.how} {CREATIVE_HINT}</p>
                        </div>
                        <Field label="Title" required error={fieldErrors.title} hint={`Max ${ADS_LIMITS.TITLE} characters.`}>
                            <input name="title" maxLength={ADS_LIMITS.TITLE} value={bannerForm.title} onChange={onField(setBannerForm)} aria-invalid={Boolean(fieldErrors.title)} />
                        </Field>
                        <div className="ads-field">
                            <span className="ads-label">
                                <label htmlFor="ads-creative-file">Ad image</label>
                                <span className="ads-required">Required</span>
                            </span>
                            <div className="ads-file">
                                <input
                                    id="ads-creative-file"
                                    className="ads-file-input"
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                                    aria-label="Ad image"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0] || null;
                                        const check = validateCreativeFile(file);
                                        if (file && !check.ok) {
                                            setFieldErrors((prev) => ({ ...prev, imageUrl: check.message }));
                                            setError(check.message);
                                            setCreativeFile(null);
                                            event.target.value = "";
                                            return;
                                        }
                                        setError("");
                                        setCreativeFile(file);
                                    }}
                                />
                                <label htmlFor="ads-creative-file" className="ads-btn ads-btn-secondary">Choose image</label>
                                <span className="ads-file-name">{creativeFile?.name || "No file chosen"}</span>
                            </div>
                            {fieldErrors.imageUrl ? <span className="ads-field-error">{fieldErrors.imageUrl}</span> : <span className="ads-hint">{CREATIVE_HINT}</span>}
                        </div>
                        {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.CREATE) && (
                        <div className="ads-actions">
                            <button type="button" className="ads-btn ads-btn-secondary" disabled={saving} onClick={uploadCreative}>Upload image</button>
                        </div>
                        )}
                        {bannerForm.imageUrl ? (
                            <div data-testid="banner-image-ready">
                                <AdsSlotPreview imageUrl={bannerForm.imageUrl} placement={bannerForm.placement} title={bannerForm.title} />
                            </div>
                        ) : (
                            <p className="ads-empty-copy">Choose a file, then upload. You do not paste an image URL.</p>
                        )}
                        <Field label="Link when tapped" error={fieldErrors.targetUrl}>
                            <input name="targetUrl" maxLength={ADS_LIMITS.URL} value={bannerForm.targetUrl} onChange={onField(setBannerForm)} placeholder="https://" aria-invalid={Boolean(fieldErrors.targetUrl)} />
                        </Field>
                        <Field label="Sponsorship" hint="Leave as club house ad if this is not sold as a deal.">
                            <select name="sponsorshipId" value={bannerForm.sponsorshipId} onChange={onField(setBannerForm)}>
                                <option value="">Club house ad</option>
                                {sponsorships.map((deal) => (
                                    <option key={deal.id} value={deal.id}>{dealLabel(deal, sponsors)}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Placement" hint="Where the app will show this ad." error={fieldErrors.placement}>
                            <select name="placement" value={bannerForm.placement} onChange={onField(setBannerForm)} aria-invalid={Boolean(fieldErrors.placement)}>
                                {allowedPlacements.map((placement) => <option key={placement} value={placement}>{placementLabel(placement)}</option>)}
                            </select>
                        </Field>
                        <Field label="Ad type">
                            <select name="adType" value={bannerForm.adType} onChange={onField(setBannerForm)}>
                                <option value="IMAGE">Image</option>
                                <option value="VIDEO">Video</option>
                                <option value="CAROUSEL">Carousel</option>
                            </select>
                        </Field>
                        {bannerForm.adType === "VIDEO" && (
                            <Field label="Video URL" hint="HTTPS link to a hosted video. We do not upload video files." error={fieldErrors.videoUrl}>
                                <input name="videoUrl" maxLength={ADS_LIMITS.URL} value={bannerForm.videoUrl} onChange={onField(setBannerForm)} placeholder="https://" aria-invalid={Boolean(fieldErrors.videoUrl)} />
                            </Field>
                        )}
                        <Field label="Campaign" hint="Optional. Groups this banner under a campaign you already created.">
                            <select name="campaignId" value={bannerForm.campaignId} onChange={onField(setBannerForm)}>
                                <option value="">None</option>
                                {campaigns.map((campaign) => (
                                    <option key={campaignIdOf(campaign)} value={campaignIdOf(campaign)}>{campaign.name}</option>
                                ))}
                            </select>
                        </Field>
                        <label className="ads-field ads-field-check">
                            <input name="active" type="checkbox" checked={bannerForm.active} onChange={onField(setBannerForm)} />
                            <span className="ads-label">Active</span>
                        </label>
                        <div className="ads-actions ads-actions-end">
                            <button type="submit" className="ads-btn ads-btn-primary" disabled={saving}>{editingBannerId ? "Update banner" : "Save banner"}</button>
                            {editingBannerId && (
                                <button
                                    type="button"
                                    className="ads-btn ads-btn-secondary"
                                    onClick={() => {
                                        setEditingBannerId("");
                                        setBannerForm(emptyBannerForm());
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                            <button type="button" className="ads-btn ads-btn-ghost" onClick={previewBanners}>Preview</button>
                        </div>
                    </form>
                    )}
                    <div className="ads-card">
                        <h2>Banners</h2>
                        <p className="ads-card-lead">{ADS_TAB_COPY.Banners.meaning}</p>
                        {banners.length === 0 ? <p className="ads-empty-copy">No banners yet.</p> : (
                            <ul className="ads-list">
                                {banners.map((banner) => (
                                    <li key={bannerIdOf(banner)}>
                                        <span className="ads-list-copy">{banner.title} · {placementLabel(banner.placement)}</span>
                                        <span className="ads-actions">
                                            {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY) && (
                                            <button type="button" className="ads-btn ads-btn-ghost" onClick={() => editBanner(banner)}>Edit</button>
                                            )}
                                            {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY) && (banner.active !== false ? (
                                                <button type="button" className="ads-btn ads-btn-secondary" onClick={() => runListedAction(() => AdvertisementAPI.deactivateBanner(bannerIdOf(banner), clubId))}>Deactivate</button>
                                            ) : (
                                                <button type="button" className="ads-btn ads-btn-ghost" onClick={() => runListedAction(() => AdvertisementAPI.activateBanner(bannerIdOf(banner), clubId))}>Activate</button>
                                            ))}
                                            {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.DELETE) && (
                                            <button
                                                type="button"
                                                className="ads-btn ads-btn-danger"
                                                disabled={saving}
                                                onClick={() => askDelete(
                                                    "Delete banner?",
                                                    `Delete ${banner.title || "this banner"}? This cannot be undone.`,
                                                    () => AdvertisementAPI.deleteBanner(bannerIdOf(banner), clubId)
                                                )}
                                            >
                                                Delete
                                            </button>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {tab === "Preview" && (
                <div className="ads-card ads-preview-panel">
                    <AdsAppPreview
                        clubName={selectedClub?.clubName || "Your club"}
                        isFreePlan={selectedClub?.freePlanUser !== false}
                        sponsorships={sponsorships}
                        loadDisplay={loadDisplay}
                        initialScreen={previewScreen}
                    />
                </div>
            )}

            {tab === "Campaigns" && (
                <div className="ads-panel">
                    {(canDo(ads.MANAGE_ADVERTISEMENT, Privileges.CREATE) || (editingCampaignId && canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY))) && (
                    <form className="ads-card ads-form" onSubmit={saveCampaign} noValidate>
                        <div>
                            <h2>{editingCampaignId ? "Edit campaign" : "Add campaign"}</h2>
                            <p className="ads-card-lead">{ADS_TAB_COPY.Campaigns.how}</p>
                        </div>
                        <Field label="Name" required error={fieldErrors.name} hint={`Max ${ADS_LIMITS.NAME} characters.`}>
                            <input name="name" maxLength={ADS_LIMITS.NAME} value={campaignForm.name} onChange={onField(setCampaignForm)} aria-invalid={Boolean(fieldErrors.name)} />
                        </Field>
                        <Field label="Description" hint={`Max ${ADS_LIMITS.DESCRIPTION} characters.`} error={fieldErrors.description}>
                            <textarea name="description" maxLength={ADS_LIMITS.DESCRIPTION} value={campaignForm.description} onChange={onField(setCampaignForm)} />
                        </Field>
                        <Field label="Start" required error={fieldErrors.startDate}>
                            <input name="startDate" type="datetime-local" value={campaignForm.startDate} onChange={onField(setCampaignForm)} aria-invalid={Boolean(fieldErrors.startDate)} />
                        </Field>
                        <Field label="End" required error={fieldErrors.endDate}>
                            <input name="endDate" type="datetime-local" value={campaignForm.endDate} onChange={onField(setCampaignForm)} aria-invalid={Boolean(fieldErrors.endDate)} />
                        </Field>
                        <Field label="Total budget" required hint="Required. Must be greater than zero." error={fieldErrors.totalBudget}>
                            <input name="totalBudget" type="number" min="0.01" max={ADS_LIMITS.MONEY_MAX} step="0.01" value={campaignForm.totalBudget} onChange={onField(setCampaignForm)} aria-invalid={Boolean(fieldErrors.totalBudget)} />
                        </Field>
                        <Field label="Daily budget" hint="Optional. Cannot exceed the total budget." error={fieldErrors.dailyBudget}>
                            <input name="dailyBudget" type="number" min="0.01" max={ADS_LIMITS.DAILY_MONEY_MAX} step="0.01" value={campaignForm.dailyBudget} onChange={onField(setCampaignForm)} aria-invalid={Boolean(fieldErrors.dailyBudget)} />
                        </Field>
                        <Field label="Target audience" hint={`Optional. Max ${ADS_LIMITS.AUDIENCE} characters.`} error={fieldErrors.targetAudience}>
                            <input name="targetAudience" maxLength={ADS_LIMITS.AUDIENCE} value={campaignForm.targetAudience} onChange={onField(setCampaignForm)} />
                        </Field>
                        <Field label="Status">
                            <select name="status" value={campaignForm.status} onChange={onField(setCampaignForm)}>
                                {CAMPAIGN_STATUSES.map((status) => <option key={status} value={status}>{humanEnum(status)}</option>)}
                            </select>
                        </Field>
                        <div className="ads-actions ads-actions-end">
                            <button type="submit" className="ads-btn ads-btn-primary" disabled={saving}>{editingCampaignId ? "Update campaign" : "Save campaign"}</button>
                            {editingCampaignId && (
                                <button
                                    type="button"
                                    className="ads-btn ads-btn-secondary"
                                    onClick={() => {
                                        setEditingCampaignId("");
                                        setCampaignForm(emptyCampaignForm());
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                    )}
                    <div className="ads-card">
                        <h2>Campaigns</h2>
                        <p className="ads-card-lead">{ADS_TAB_COPY.Campaigns.meaning}</p>
                        {campaigns.length === 0 ? <p className="ads-empty-copy">No campaigns yet.</p> : (
                            <ul className="ads-list">
                                {campaigns.map((campaign) => (
                                    <li key={campaignIdOf(campaign)}>
                                        <span className="ads-list-copy">{campaign.name} · {humanEnum(campaign.status)}</span>
                                        <span className="ads-actions">
                                            {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY) && (
                                                <button type="button" className="ads-btn ads-btn-ghost" disabled={saving} onClick={() => editCampaign(campaign)}>Edit</button>
                                            )}
                                            {campaign.status === "ACTIVE" && canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY) && (
                                                <button type="button" className="ads-btn ads-btn-secondary" disabled={saving} onClick={() => runListedAction(() => AdvertisementAPI.pauseCampaign(campaignIdOf(campaign), clubId))}>Pause</button>
                                            )}
                                            {(campaign.status === "DRAFT" || campaign.status === "PAUSED") && canDo(ads.MANAGE_ADVERTISEMENT, Privileges.MODIFY) && (
                                                <button type="button" className="ads-btn ads-btn-secondary" disabled={saving} onClick={() => runListedAction(() => AdvertisementAPI.activateCampaign(campaignIdOf(campaign), clubId))}>Activate</button>
                                            )}
                                            {canDo(ads.MANAGE_ADVERTISEMENT, Privileges.DELETE) && (
                                                <button
                                                    type="button"
                                                    className="ads-btn ads-btn-danger"
                                                    disabled={saving}
                                                    onClick={() => askDelete(
                                                        "Delete campaign?",
                                                        `Delete ${campaign.name || "this campaign"}? This cannot be undone.`,
                                                        () => AdvertisementAPI.deleteCampaign(campaignIdOf(campaign), clubId)
                                                    )}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            <CustomDialog
                title={confirmDelete.title}
                isOpen={confirmDelete.open}
                submitBtnName="Yes, delete"
                cancelBtnName="Keep"
                onSubmit={confirmDeleteNow}
                onCancel={closeConfirm}
                onClose={closeConfirm}
                disableSubmitBtn={saving}
            >
                <p>{confirmDelete.message}</p>
            </CustomDialog>
        </div>
    );
};

export default AdsSponsorship;
